import React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const selectSx = { width: '190px', height: '30px' };
const textFieldSx = {
  width: '190px',
  height: '30px',
  padding: 0,
  '& .MuiInputBase-root': { border: '1px solid #ced4da', borderRadius: '4px' },
  marginTop: '5px',
};

const ClearableDatePicker = ({ value, onChange, onClear, label, disabled }) => (
  <DatePicker
    value={value}
    onChange={onChange}
    disabled={disabled}
    format="DD/MM/YYYY"
    sx={{ flexGrow: 1 }}
    renderInput={(props) => (
      <TextField
        {...props}
        label={label}
        InputProps={{
          ...props.InputProps,
          endAdornment: (
            <InputAdornment position="end">
              {value && (
                <IconButton onClick={onClear} sx={{ marginRight: '-25px' }}>
                  <CancelIcon />
                </IconButton>
              )}
              {props.InputProps.endAdornment}
            </InputAdornment>
          ),
        }}
        sx={{
          width: '190px',
          paddingRight: 0,
          paddingBottom: 3,
          '& .MuiSvgIcon-root': { marginRight: '5px' },
          '& .MuiInputBase-root': { border: '1px solid #ced4da', borderRadius: '4px' },
        }}
      />
    )}
  />
);

// Dispatcher: dato un field-config, ritorna il controllo giusto (testo / select / date range)
const FieldValueControl = ({ field }) => {
  if (!field) return null;

  if (field.type === 'text') {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'start' }}>
        <Select value={field.searchType} onChange={field.onSearchTypeChange} sx={selectSx}>
          {field.searchTypeOptions.map((o, i) => <MenuItem key={i} value={o}>{o}</MenuItem>)}
        </Select>
        <TextField
          margin="normal" fullWidth variant="standard" autoFocus
          value={field.value} onChange={field.onChange} sx={textFieldSx}
        />
        <IconButton onClick={field.onClear} title="Pulisci"><CancelIcon /></IconButton>
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'start' }}>
        <Select value={field.searchType} onChange={field.onSearchTypeChange} sx={selectSx}>
          {field.searchTypeOptions.map((o, i) => <MenuItem key={i} value={o}>{o}</MenuItem>)}
        </Select>
        <Select value={field.value} onChange={field.onChange} sx={selectSx} disabled={field.disabled}>
          {field.options.map((o, i) => <MenuItem key={i} value={o}>{o}</MenuItem>)}
        </Select>
        <IconButton onClick={field.onClear} title="Pulisci"><CancelIcon /></IconButton>
      </div>
    );
  }

  if (field.type === 'dateRange') {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'start' }}>
        <Select value={field.dateSearchType} onChange={field.onDateSearchTypeChange} sx={selectSx}>
          {field.dateSearchTypeOptions.map((o, i) => <MenuItem key={i} value={o}>{o}</MenuItem>)}
        </Select>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'start', justifyContent: 'start' }}>
            <ClearableDatePicker
              value={field.valueDa} onChange={field.onChangeDa} onClear={field.onClearDa}
              label="Da" disabled={field.disabled}
            />
            {field.dateSearchType === 'Periodo' && (
              <ClearableDatePicker
                value={field.valueA} onChange={field.onChangeA} onClear={field.onClearA}
                label="A" disabled={field.disabled}
              />
            )}
          </div>
        </LocalizationProvider>
      </div>
    );
  }

  return null;
};

const DynamicFilterMenu = ({
  anchorEl, open, handleClose, handleAdd, handleRemove, handleFiltra, handleDownloadExcel,
  resetToInitialList, isFiltering,
  fields,            // array di configurazione (vedi doc sopra)
  activeColumns,     // array di label selezionate, una per riga attiva
  onColumnChange,    // (rowIndex, nuovaLabel) => void
  count,
  maxFilters,
}) => {
  const fieldLabels = fields.map(f => f.label);
  const limit = maxFilters ?? fields.length;

  const getFieldByLabel = (label) => fields.find(f => f.label === label);

  return (
    <Menu
      id="basic-menu"
      sx={{ width: '100%', marginTop: '-100px' }}
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      MenuListProps={{ 'aria-labelledby': 'basic-button' }}
    >
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <div>
          <IconButton onClick={handleAdd} disabled={count >= limit} title="Aggiungi Filtro"><AddIcon /></IconButton>
          <IconButton onClick={handleRemove} disabled={count === 0} title="Rimuovi Filtro"><RemoveIcon /></IconButton>
          <IconButton onClick={resetToInitialList} disabled={count === 0} title="Annulla Filtraggio"><CancelIcon /></IconButton>
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
          <IconButton onClick={handleFiltra} title="Cerca"><SearchIcon /></IconButton>
          <IconButton onClick={handleDownloadExcel} disabled={!isFiltering} title="Download Excel"><FileDownloadIcon /></IconButton>
        </div>
      </div>

      <div style={{ margin: '10px' }}>
        {Array.from({ length: count }).map((_, rowIndex) => {
          const selectedLabel = activeColumns[rowIndex];
          const previousLabels = rowIndex === 0 && count === 1 ? [] : activeColumns.slice(0, rowIndex).filter(Boolean);

          return (
            <div
              key={rowIndex}
              style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: rowIndex === 0 ? 0 : '10px' }}
            >
              <Select
                value={selectedLabel || ''}
                onChange={(e) => onColumnChange(rowIndex, e.target.value)}
                sx={selectSx}
              >
                {fieldLabels
                  .filter(label => !previousLabels.includes(label))
                  .map((label, i) => (
                    <MenuItem key={i} value={label}>{label}</MenuItem>
                  ))}
              </Select>

              <FieldValueControl field={getFieldByLabel(selectedLabel)} />
            </div>
          );
        })}
      </div>
    </Menu>
  );
};

export default DynamicFilterMenu;
